import { Fragment, useEffect } from 'react';

import { MoonIcon, SunIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FieldsetCard } from '@/components/atoms/FieldsetCard/FieldsetCard';
import { SectionLayout } from '@/components/atoms/SectionLayout/SectionLayout';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePreferences } from '@/hooks/usePreferences';
import { useRegisterSectionSave } from '@/hooks/useRegisterSectionSave';
import { useTrackFormState } from '@/hooks/useTrackFormState';
import { DARK_THEMES, THEMES } from '@/shared/constants/theme';
import { Theme } from '@/shared/types/user';
import { CLEAR_DIRTY_VALUES, KEEP_DIRTY_VALUES } from '@/util/forms/resetOptions';
import { applyTheme, previewTheme, resolveTheme, restoreTheme } from '@/util/theme';

const FORM_ID = 'profile-preferences-form';

type Form = { theme: Theme };

const THEME_LABEL_KEYS: Record<Theme, string> = {
  'gold-light': 'profile.interface_theme.gold',
  'gold-dark': 'profile.interface_theme.gold',
  light: 'profile.interface_theme.classic',
  dark: 'profile.interface_theme.classic',
  'solarized-light': 'profile.interface_theme.solarized',
  'solarized-dark': 'profile.interface_theme.solarized',
  'catppuccin-latte': 'profile.interface_theme.catppuccin',
  'catppuccin-mocha': 'profile.interface_theme.catppuccin',
};

/** Scoping the theme to the pill makes its role tokens resolve to that theme's colors. */
const ThemeSwatch = ({ theme }: { theme: Theme }) => (
  <span
    data-theme={theme}
    data-theme-scope
    aria-hidden
    className="ml-auto flex items-center gap-1 rounded-full border border-border bg-background p-1"
  >
    <span className="size-2.5 rounded-full bg-primary" />
    <span className="size-2.5 rounded-full bg-muted-foreground" />
    <span className="size-2.5 rounded-full bg-foreground" />
  </span>
);

const THEME_GROUPS = [
  {
    icon: SunIcon,
    labelKey: 'profile.interface_theme.group_light',
    themes: THEMES.filter((theme) => !DARK_THEMES.includes(theme)),
  },
  { icon: MoonIcon, labelKey: 'profile.interface_theme.group_dark', themes: DARK_THEMES },
];

export const PreferencesSection = () => {
  const { t } = useTranslation();
  const { userPreferences, updateUserPreferencesAsync } = usePreferences();

  const form = useForm<Form>({
    resetOptions: KEEP_DIRTY_VALUES,
    values: { theme: resolveTheme(userPreferences?.theme) },
  });
  const {
    control,
    handleSubmit,
    getValues,
    reset,
    formState: { isDirty, isSubmitting },
  } = form;

  useTrackFormState({ isDirty, formId: FORM_ID });

  // Leaving the page drops an unsaved preview and puts back the saved theme.
  useEffect(() => restoreTheme, []);

  const endPreview = () => previewTheme(getValues('theme'));

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      endPreview();
    }
  };

  const onSubmit = async (data: Form) => {
    await updateUserPreferencesAsync({ theme: data.theme });
    applyTheme(data.theme);
    reset(data, CLEAR_DIRTY_VALUES);
  };

  useRegisterSectionSave(FORM_ID, isDirty, handleSubmit, onSubmit);

  return (
    <SectionLayout title={t('profile.preferences')}>
      <Form {...form}>
        <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)}>
          <FieldsetCard
            isDirty={isDirty}
            isLoading={isSubmitting}
            disabled={!isDirty}
            saveButtonLabel={t('common.save')}
            formId={FORM_ID}
          >
            <FormField
              control={control}
              name="theme"
              render={({ field }) => (
                <FormItem className="flex flex-col gap-3 space-y-0 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
                  <div className="flex min-w-0 flex-col gap-1">
                    <FormLabel className="mb-0 leading-none text-foreground">
                      {t('profile.interface_theme.title')}
                    </FormLabel>
                    <FormDescription>{t('profile.interface_theme.subtitle')}</FormDescription>
                  </div>
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    onOpenChange={handleOpenChange}
                  >
                    <FormControl>
                      <SelectTrigger
                        hideChevron
                        className="w-full *:data-[slot=select-value]:flex-1 sm:w-48 sm:shrink-0"
                      >
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent align="end" className="w-60" onPointerLeave={endPreview}>
                      {THEME_GROUPS.map((group, index) => (
                        <Fragment key={group.labelKey}>
                          {index > 0 && <SelectSeparator />}
                          <SelectGroup>
                            <SelectLabel>{t(group.labelKey)}</SelectLabel>
                            {group.themes.map((theme) => (
                              <SelectItem
                                key={theme}
                                value={theme}
                                indicatorPosition="none"
                                // Radix focuses the option under the pointer or arrow keys.
                                onFocus={() => previewTheme(theme)}
                                className="*:[span]:last:flex-1"
                              >
                                <group.icon className="size-4" />
                                {t(THEME_LABEL_KEYS[theme])}{' '}
                                <span className="sr-only">{t(group.labelKey)}</span>
                                <ThemeSwatch theme={theme} />
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </Fragment>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
          </FieldsetCard>
        </form>
      </Form>
    </SectionLayout>
  );
};
